import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard'
import CreateIcon from '@mui/icons-material/Create'
import TimelineIcon from '@mui/icons-material/Timeline'
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import PaymentIcon from '@mui/icons-material/Payment'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import TuneIcon from '@mui/icons-material/Tune'
import CodeIcon from '@mui/icons-material/Code'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import { APP_VERSION } from '../services/appVersion'

// Fonte única da navegação. As abas do topo, o menu "Mais" e a barra inferior
// do celular saem todos daqui — assim não existe a chance de uma das três
// listas divergir das outras quando entrar uma página nova.
//
// Campos de cada item:
//   to          rota (sempre navegada via LinkWithQuery, para não perder a query)
//   label       rótulo completo, usado no topo e no "Mais"
//   labelCurto  rótulo da barra inferior do celular, onde só cabem ~9 caracteres
//   descricao   frase do cartão de ação da tela inicial
//   fixa        true = vira aba no topo e botão na barra inferior
//   naHome      true = vira cartão de ação na tela inicial
//   requer      funcionalidade exigida em user.plan.functionalities
//   badge       selo opcional ao lado do rótulo dentro do "Mais"

export const NAV_ITEMS = [
  {
    to: '/',
    label: 'Início',
    labelCurto: 'Início',
    descricao: 'Visão geral da sua conta',
    icon: SpaceDashboardIcon,
    fixa: true,
    naHome: false,
    requer: null
  },
  {
    to: '/messages/send',
    label: 'Enviar mensagem',
    labelCurto: 'Enviar',
    descricao: 'Dispare um modelo ou mensagem para uma lista de clientes',
    icon: CreateIcon,
    fixa: true,
    naHome: true,
    requer: null
  },
  {
    to: '/reports',
    label: 'Relatórios',
    labelCurto: 'Relatórios',
    descricao: 'Quantas mensagens saíram, chegaram e foram lidas',
    icon: TimelineIcon,
    fixa: true,
    naHome: true,
    requer: null
  },
  {
    to: '/messages/scheduled',
    label: 'Agendamentos',
    labelCurto: 'Agendar',
    descricao: 'Programe mensagens para uma data e hora',
    icon: ScheduleSendIcon,
    fixa: true,
    naHome: true,
    requer: 'ScheduledMessages'
  },
  {
    to: '/hiperpay/profiles',
    label: 'Cobranças',
    labelCurto: 'Cobranças',
    descricao: 'Boletos e Pix enviados pelo WhatsApp',
    icon: PaymentIcon,
    fixa: true,
    naHome: true,
    requer: 'HiperPay'
  },
  {
    to: '/hipersync',
    label: 'Piloto Automático',
    labelCurto: 'Piloto',
    descricao: 'Lembretes de cobrança conectados ao seu sistema',
    icon: ScheduleSendIcon,
    fixa: false,
    naHome: true,
    requer: 'HiperSync'
  },
  {
    to: '/smart-filter',
    label: 'Filtro de números',
    labelCurto: 'Filtro',
    descricao: 'Evite enviar para quem não deve receber',
    icon: FilterAltIcon,
    fixa: false,
    naHome: true,
    requer: 'SmartFilter'
  },
  {
    to: '/messages/scheduler',
    label: 'Horários de envio',
    labelCurto: 'Horários',
    descricao: 'Defina a janela em que a API pode disparar',
    icon: WatchLaterIcon,
    fixa: false,
    naHome: true,
    requer: null
  },
  {
    to: '/settings',
    label: 'Configurações',
    labelCurto: 'Ajustes',
    descricao: 'Nome, tema e preferências da sua API',
    icon: TuneIcon,
    fixa: false,
    naHome: true,
    requer: null
  },
  {
    to: '/documentation',
    label: 'Para desenvolvedores',
    labelCurto: 'Dev',
    descricao: 'Documentação da API para quem vai integrar',
    icon: CodeIcon,
    fixa: false,
    naHome: false,
    requer: null,
    badge: 'NOVIDADES'
  },
  {
    to: '/changelog',
    label: 'Novidades da versão',
    labelCurto: 'Novidades',
    descricao: 'O que mudou nas últimas atualizações',
    icon: AccountTreeIcon,
    fixa: false,
    naHome: false,
    requer: null,
    badge: `v${APP_VERSION}`
  }
]

// Quantas entradas cabem na barra inferior do celular antes do botão "Mais".
export const LIMITE_BARRA_INFERIOR = 4

function temFuncionalidade(user, funcionalidade) {
  if (!funcionalidade) return true
  return user?.plan?.functionalities?.includes(funcionalidade) || false
}

// Remove o que o plano do cliente não contempla. Quem não tem HiperPay não
// enxerga "Cobranças" em lugar nenhum — nem aba, nem "Mais", nem cartão.
export function itensVisiveis(user) {
  return NAV_ITEMS.filter(item => temFuncionalidade(user, item.requer))
}

// Abas do topo. Como os itens gated somem, um cliente de plano básico acaba
// com menos abas — e o topo simplesmente fica mais curto, sem buraco.
export function itensDoTopo(user) {
  return itensVisiveis(user).filter(item => item.fixa)
}

// Tudo que não virou aba cai no "Mais".
export function itensDoMais(user) {
  return itensVisiveis(user).filter(item => !item.fixa)
}

// Barra inferior do celular: as primeiras entradas fixas, respeitando o limite.
export function itensDaBarraInferior(user) {
  return itensDoTopo(user).slice(0, LIMITE_BARRA_INFERIOR)
}

// O que sobra vai para a folha aberta pelo botão "Mais" do celular: as abas que
// não couberam na barra somadas aos itens que já eram de "Mais".
export function itensDaFolhaMais(user) {
  return [
    ...itensDoTopo(user).slice(LIMITE_BARRA_INFERIOR),
    ...itensDoMais(user)
  ]
}

// Cartões de ação da tela inicial.
export function itensDaHome(user) {
  return itensVisiveis(user).filter(item => item.naHome)
}

// A aba ativa sai da rota, não de estado interno. Rota filha mantém a aba pai
// acesa — "/reports/123" continua marcando "Relatórios".
export function itemEstaAtivo(item, pathname) {
  if (item.to === '/') return pathname === '/'
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}
